/**
 * #580 D1 (#903) — a family turn's interactions, client half: the renewal POST, and the one channel by
 * which a screen tells the turn's return (`state/ChildActorReturn.jsx`) what the server answered.
 *
 * The server renews a family turn to "receipt time + 60 s" (d-20260929-04) on two things only: this
 * CSRF-protected POST, which names the turn's generation and a `sequence` above the last one it
 * accepted for that generation (`server/schemas/profile.schemas.js` `familyInteractionBody`), and a SAVED
 * completion or undo (`familyTurn.renewOnWrite`), which takes no body field. A read never renews. Tabs on
 * one cookie share the count by drawing from one rising source: the clock in ms, never below the last
 * number this page sent or learned + 1. A lower number from another tab is stale and buys nothing, so the
 * turn's return sends one catch-up above the number the server keeps, after a first send only.
 *
 * The POST is sent by the turn's return for a trusted tap only (`inputPolicy`): its first send, then for that same
 * tap at most one retry, from a timer, and one catch-up. Never for a read or a socket. A screen reports here what a
 * write's answer said about the turn: a later deadline after a saved write, or the server ending the turn.
 */
import { ApiError, api } from '../api/client.js';
import { ACTOR_INTERACTION_PATH } from '../api/session.js';

/** The kiosk's interaction path, which a family turn shares (`api/session.js`). */
export { ACTOR_INTERACTION_PATH as FAMILY_INTERACTION_PATH };

/**
 * The answers that end the turn a request named: expired or locked (`ACTOR_REQUIRED`), another generation
 * or member (`ACTOR_INVALID`, or `FAMILY_TURN_FORBIDDEN` on the member's own read), and revoked.
 */
const ENDED = new Map([
  ['ACTOR_REQUIRED', 'locked'],
  ['ACTOR_INVALID', 'locked'],
  ['FAMILY_TURN_FORBIDDEN', 'locked'],
  ['FAMILY_TURN_REVOKED', 'revoked'],
]);

/** `'locked'` or `'revoked'` when `error` is the server ending the turn, otherwise `null`. */
export function turnEndedBy(error) {
  if (!(error instanceof ApiError) || (error.status !== 401 && error.status !== 403)) return null;
  return ENDED.get(error.body?.code) ?? null;
}

/**
 * #1003 — the ONE sentence a screen shows when a family turn's read did not answer, so Today, Trophies and Village
 * say the same thing. `forbidden` names the read (the turn may not make it); the other four are the same whichever
 * read asked. Only `failed`, an allowed read that did not answer, may suggest trying again: waiting never opens a
 * read the turn does not make.
 */
const TURN_READ_REFUSED = Object.freeze({
  echo: "Approval updates aren't available during this turn.",
  streak: "Streak isn't available during this turn.",
  level: "Story level isn't available during this turn.",
  trophies: "Your trophies aren't available during this turn.",
  village: "Village isn't available during this turn.",
  // The Village goal shows, and the story read does not (story credits off): it names no contributors.
  villageParts: "You can see your family's shared goal here. Who is connected, contributors and individual counts aren't available during this turn.",
  // The goal and the story show: the story names contributors, so only presence and the counts are missing.
  villagePartsStory: "You can see your family's shared goal and story here. Who is connected and individual counts aren't available during this turn.",
});
const TURN_READ_BY_STATUS = Object.freeze({
  locked: 'Your turn has ended. Choose yourself and enter your PIN again.',
  revoked: 'This session has ended. Sign in again.',
  stale: 'This turn has changed. Choose yourself again.',
  failed: "We couldn't load this. Try again.",
});

/**
 * The sentence for `kind`'s read and why it did not answer (`turnReadStatus`), or `null` for a pair it does not know.
 * Only `forbidden` needs the kind; the other four are said the same for any read, so a caller with no named read
 * (Today's day) passes `null`.
 */
export function turnReadMessage(kind, status) {
  const own = (table, key) => (Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null);
  return status === 'forbidden' ? own(TURN_READ_REFUSED, kind) : own(TURN_READ_BY_STATUS, status);
}

/**
 * Why a family turn's read failed, as `turnReadMessage` names it: the server's turn refusals (or the API client's
 * copy of them, `FamilyTurnScopeError`), else `failed`. `FAMILY_TURN_FORBIDDEN` is `forbidden`: the server says the
 * same for a read the turn does not open and for a turn that is no longer live, and a screen that knows the turn has
 * ended says `locked` itself.
 */
export function turnReadStatus(error) {
  if (error instanceof ApiError) {
    const code = error.body?.code;
    if (error.status === 401 && code === 'FAMILY_TURN_REVOKED') return 'revoked';
    if (error.status === 403 && code === 'ACTOR_INVALID') return 'stale';
    if (error.status === 403 && code === 'ACTOR_REQUIRED') return 'locked';
    if (error.status === 403 && code === 'FAMILY_TURN_FORBIDDEN') return 'forbidden';
  }
  return 'failed';
}

/**
 * `turnReadStatus`, except that a 401 or 403 naming no turn code is `forbidden`, not `failed`. After the turn gate
 * accepts, a service can still refuse (the member was deactivated or removed in between), and asking again does not
 * open that. Network faults, aborts, a 404 and a 5xx stay `failed`.
 */
export function turnReadRefusal(error) {
  const status = turnReadStatus(error);
  if (status !== 'failed') return status;
  return error instanceof ApiError && (error.status === 401 || error.status === 403) ? 'forbidden' : 'failed';
}

/** The last renewal number this page sent or learned, for one generation. */
let counted = { generation: null, last: 0 };

/** The next renewal number for the turn `generation`: above every one this page sent or learned. */
export function nextRenewalSequence(generation) {
  if (counted.generation !== generation) counted = { generation, last: 0 };
  counted.last = Math.max(Date.now(), counted.last + 1);
  return counted.last;
}

/**
 * Report ONE deliberate interaction for the turn `generation`. A retry passes the `sequence` its first send
 * drew: the server accepts a number it already has without renewing again, so one tap renews once.
 *
 * @returns {Promise<{renewed:boolean, actingProfile:object|null, familyTurn:object}|{ended:'locked'|'revoked'}>}
 *   the server's answer, or how the server ended the turn. Any other failure is thrown.
 */
export async function renewFamilyTurn(generation, kind = 'activate-control', sequence = nextRenewalSequence(generation)) {
  let payload;
  try {
    payload = await api.post(ACTOR_INTERACTION_PATH, { generation, sequence, kind });
  } catch (error) {
    const ended = turnEndedBy(error);
    if (ended) return { ended };
    throw error;
  }
  const accepted = Number(payload?.familyTurn?.interactionSequence);
  if (counted.generation === generation && Number.isSafeInteger(accepted)) counted.last = Math.max(counted.last, accepted);
  return payload;
}

const listeners = new Set();

/** @param {(generation: string, news: {expiresAt?: string, ended?: 'locked'|'revoked'}) => void} listener */
export function subscribeFamilyTurn(listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * What a screen learned about the turn `generation` from the server: `{ expiresAt }` after a saved write
 * renewed it, or `{ ended }`. Tagged with the generation the request named, so an answer for an earlier
 * turn changes nothing about the next one. No generation (no turn) reports nothing.
 */
export function reportFamilyTurn(generation, news) {
  if (!generation) return;
  for (const listener of [...listeners]) listener(generation, news);
}

/** Report `error` when it is the server ending the turn `generation`. Returns how it ended, or `null`. */
export function reportTurnRefusal(generation, error) {
  const ended = generation ? turnEndedBy(error) : null;
  if (ended) reportFamilyTurn(generation, { ended });
  return ended;
}
